import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { routes } from "@/app/navigation/routes";
import { heroImage } from "@/assets/images";
import { AddressSearch, AddressSuggestion } from "@/shared/components/common/AddressSearch";
import { propertySupportService } from "@/features/properties/model/propertyServices";
import styles from "./heroSection.module.css";
import { useAuth } from "@/app/providers/useAuth";
import { tokenService } from "@/services/auth/token.service";
import { useAddressListViewModel } from "@/shared/components/common/AddressSelectPill/AdrressListviewmodel";
import { getGuestProperties } from "@/features/properties/model/guestSession";

/**
 * HeroSection Component
 * Main hero section with address input and CTA
 */
export const HeroSection = () => {
  const searchAddresses = useCallback(async (query: string): Promise<AddressSuggestion[]> => {
    if (!query || query.trim().length < 3) {
      return [];
    }

    try {
      const results = await propertySupportService.getRealStateAutoCompletePropertyDetails({
        search: query.trim(),
        search_types: ["A"],
      });

      console.log("API results:", results);

      if (!results || !Array.isArray(results) || results.length === 0) {
        console.log("No results returned from API");
        return [];
      }

      const mappedResults = results
        .map((item): AddressSuggestion | null => {
          // Build address string from available fields
          const addressParts: string[] = [];

          if (item.house) addressParts.push(item.house.trim());
          if (item.street) addressParts.push(item.street.trim());

          const streetAddress = addressParts.join(" ").trim();

          // Use provided address or build from parts
          let fullAddress = item.address || "";

          if (!fullAddress && streetAddress) {
            const cityStateZip = [item.city || "", item.state || "", item.zip || ""]
              .filter(Boolean)
              .join(", ");

            fullAddress = cityStateZip ? `${streetAddress}, ${cityStateZip}`.trim() : streetAddress;
          } else if (!fullAddress) {
            // Fallback: use city, state, zip if no street address
            const parts = [item.city || "", item.state || "", item.zip || ""].filter(Boolean);
            fullAddress = parts.join(", ").trim();
          }

          // Ensure we have at least some address data
          if (!fullAddress && !item.id) {
            return null;
          }

          return {
            id: item.id || `address-${Date.now()}-${Math.random()}`,
            address: fullAddress || "Address not available",
            city: item.city,
            state: item.state,
            zipCode: item.zip,
            apn: item.apn,
          };
        })
        .filter((item): item is AddressSuggestion => item !== null);

      console.log("Mapped suggestions:", mappedResults);
      return mappedResults;
    } catch (error) {
      console.error("Error searching addresses from hero:", error);
      return [];
    }
  }, []);

  const { isAuthenticated } = useAuth();
  const hasToken = tokenService.hasToken();
  const isUserAuthenticated = isAuthenticated || hasToken;

  const { properties } = useAddressListViewModel();

  const hasPaidNotFiledProperty = useMemo(() => {
    // Check API properties for authenticated user
    const hasInApi = (properties || []).some((item) => {
      const isPaid = (item.payment_status ?? "").toLowerCase().trim() === "paid" || "null";
      const protestStatus = (item.protest_status ?? "")
        .toLowerCase()
        .trim()
        .replace(/[\s-]+/g, "_");
      const isNotFiled = protestStatus === "not_filed" || protestStatus !== "filed";
      return isPaid && isNotFiled;
    });

    if (hasInApi) return true;

    // Check guest properties
    const guestProps = getGuestProperties();
    const hasInGuest = (guestProps || []).some((item) => {
      const isPaid = (item.paymentStatus ?? "").toLowerCase().trim() === "paid";
      const isNotFiled = !item.protestFiled;
      return isPaid && isNotFiled;
    });

    return hasInGuest;
  }, [properties]);

  return (
    <section
      id="home"
      className={styles.heroSection}
      style={{
        backgroundImage: `linear-gradient(
          110deg,
          rgba(15, 23, 42, 0.52) 0%,
          rgba(45, 106, 79, 0.3) 45%,
          rgba(31, 124, 67, 0.15) 100%
        ), url(${heroImage})`,
      }}
    >
      <div className={styles.heroContent}>
        <h1 className={styles.heroTitle}>
          Effortlessly Protest
          <br />
          Your <span className={styles.texasHighlight}>Texas</span>
          <br />
          Property Taxes
        </h1>

        <div className={styles.searchContainer}>
          <AddressSearch
            placeholder="Enter address"
            buttonText="Get Estimate Savings"
            searchAddresses={searchAddresses}
            redirectTo={routes.homepage}
            onAddressSelect={(address) => {
              console.log("Address selected:", address);
            }}
            onButtonClick={(address) => {
              console.log("Button clicked with address:", address);
            }}
          />
        </div>

        {(hasPaidNotFiledProperty || !isUserAuthenticated) && (
          <div className={styles.heroActions}>
            {hasPaidNotFiledProperty && (
              <Link to={routes.homepage} className={styles.myPropertiesBtn}>
                <span>My Properties</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            )}

            {!isUserAuthenticated && (
              <div className={styles.authLinks}>
                <Link to={routes.signup} className={styles.authLink}>
                  Get Started
                </Link>
                <span className={styles.authDivider} aria-hidden="true">
                  •
                </span>
                <Link to={routes.login} className={styles.authLink}>
                  Sign In
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
